import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useHistory, useLocation } from 'react-router'
import styled from 'styled-components'

import { useApi, usePersistedApi } from 'hooks'
import { entities } from 'reduxStore'
import { exitReact } from 'utils'
import Api from 'api'

import { PurchaseTokensFlow } from "components/PurchaseTokensFlow";
import Popup from 'components/Popup'
import WhatAreTokens from 'components/WhatAreTokens'
import FixedLoader from 'components/FixedLoader'
import FixedError from 'components/FixedError'
import DentistNavbar from 'components/DentistNavbar'
import TalentBackButton from 'components/TalentBackButton/TalentBackButton'
import { JobBoard } from "components/JobBoard";
import { JobBoardModal } from "./components/JobBoardModal";
import { UserAccountProps } from "utils/types";

const PageWrapper = styled.div`
  height: 100%;
  background-color: white;
  overflow: auto;
  overflow: visible;
  position: relative;
  width: 100%;
  box-sizing: border-box;
  min-height: 100vh;
  min-height: 100dvh;
`

const ContentWrapper = styled.div`
  flex: 1;
  padding: 16px;
  max-width: 900px;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;
`

interface LocationProps {
    openPopup?: boolean
}


export default function JobBoardPage() {
    const { state } = useLocation<LocationProps>();
    const openPopup = state?.openPopup ?? false;
    const [isOpenPopup, setIsOpenPopup] = useState(openPopup);
    const [jobs, jobsLoading, jobsError, getAllHireJobs] = usePersistedApi({
        apiMethod: Api.getAllHireJobs,
        entityName: entities.dentistJobs,
        initialValue: []
    })
    const [dentistUser, dentistUserLoading, dentistUserError, getDentistUser] = usePersistedApi({
        apiMethod: Api.getUser,
        entityName: entities.user,
        initialValue: {} as UserAccountProps,
    })
    const [billingLocations, locationsLoading, locationsError, getBillingLocations] = usePersistedApi({
        apiMethod: Api.getLocations,
        entityName: entities.dentistBillingLocations,
        initialValue: [],
    })
    useEffect(() => {
        getAllHireJobs({})
        getDentistUser({})
        getBillingLocations({})
    }, [getAllHireJobs, getDentistUser, getBillingLocations])

    const updateDentistUser = useApi({
        apiMethod: Api.updateUser,
        onSuccess: useCallback(() => getDentistUser({}), [getDentistUser]),
        initialValue: {},
    })[3]

    const history = useHistory()
    const [whatAreTokensOpened, setWhatAreTokensOpened] = useState(false)
    const [tokensPurchaseOpened, setTokensPurchaseOpened] = useState(false)
    /**
     * T2-5787: don't trust persisted user state on mount. The previous mount
     * may have set `seenTokensInfo = 1` on the server, but the persisted
     * Redux snapshot still shows 0 until the next fetch resolves. Only let
     * the explainer popup decide to open AFTER a fresh fetch has completed
     * for this mount, and never re-open it once dismissed in this session.
     */
    const tokensExplainerDismissedRef = useRef(false)
    const [freshUserLoaded, setFreshUserLoaded] = useState(false)

    useEffect(() => {
        let cancelled = false
        setFreshUserLoaded(false)
        getDentistUser({}).finally(() => {
            if (!cancelled) setFreshUserLoaded(true)
        })
        return () => { cancelled = true }
    }, [getDentistUser])

    const dismissTokensExplainer = useCallback(() => {
        tokensExplainerDismissedRef.current = true
        setWhatAreTokensOpened(false)
        updateDentistUser({ seenTokensInfo: 1 })
    }, [updateDentistUser])

    useEffect(() => {
        if (tokensExplainerDismissedRef.current) return
        if (!freshUserLoaded) return
        if (dentistUser?.id && !dentistUser.seenTokensInfo) {
            if (!whatAreTokensOpened) setWhatAreTokensOpened(true)
        } else if (whatAreTokensOpened) {
            setWhatAreTokensOpened(false)
        }
    }, [dentistUser, whatAreTokensOpened, freshUserLoaded])

    // Get user info for navbar
    const currentUser = (window as any)?.TempStars?.User?.getCurrentUser?.();
    const dentist = currentUser?.dentist;
    const firstName = currentUser?.firstName || '';
    const lastName = currentUser?.lastName || '';
    const photoUrl = currentUser?.photoUrl || dentist?.photoUrl || undefined;

    const handleGetTemp = useCallback(() => {
        history.push({ pathname: 'dentistHome' });
        setTimeout(() => {
            (window as any).TempStars?.App?.goReact?.('dentistHome');
        }, 100);
    }, [history]);

    const handleBackNavigation = useCallback(() => {
        history.push({ pathname: 'dentistHome' });
    }, [history]);

    return (
        <PageWrapper>
            <DentistNavbar
                firstName={firstName}
                lastName={lastName}
                photoUrl={photoUrl}
                onGetTempClick={handleGetTemp}
                showGetTempButton={true}
            />
            {/* T2-6760: same outlined Back pill + under-logo placement as Talent Job Board */}
            <TalentBackButton onBack={handleBackNavigation} />
            <ContentWrapper>
                <JobBoard
                    jobs={jobs}
                    dentistUserEmail={dentistUser.email || ''}
                    tokensCount={dentistUser.TokenAmount || 0}
                    onTokensClick={() => {
                        setTokensPurchaseOpened(!tokensPurchaseOpened)
                    }}
                />
            </ContentWrapper>
            <Popup
                opened={whatAreTokensOpened}
            >
                <WhatAreTokens
                    onClose={dismissTokensExplainer}
                    onGotIt={dismissTokensExplainer}
                />
            </Popup>
            {tokensPurchaseOpened ? (
                <PurchaseTokensFlow
                    onClose={() => {
                        setTokensPurchaseOpened(false)
                    }}
                    onSuccess={() => {
                        setTokensPurchaseOpened(false)
                        getDentistUser({})
                    }}
                />
            ) : null}
            <FixedLoader
                loading={
                    jobsLoading
                    || dentistUserLoading
                    || locationsLoading
                }
            />
            <JobBoardModal
                isOpen={isOpenPopup && !whatAreTokensOpened}
                title={"Job Board"}
                description={"The Job Board is only for finding, recruiting, and hiring permanent or long-term contract staff. Our Terms and Services strictly prevent use of this feature for finding temps. If you're looking for a great temporary hygienist or assistant, click the 'Get a Temp' button."}
                onClose={() => setIsOpenPopup(false)}
            />
            <FixedError
                error={
                    jobsError
                    || dentistUserError
                    || locationsError
                }
            />
        </PageWrapper>
    )
}
